import {Anchor} from "../core/Anchor";
import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {ContainerExtensions} from "../core/ContainerExtensions";
import {IPoint, IRectangle} from "../core/Geom";
import {NineSlicePlane} from "../core/Mesh";
import {Callback} from "../core/Reflection";
import {Poly} from "../core/Shape";
import {Sprite} from "../core/Sprite";
import {Assets} from "../data/Assets";
import {TextBoxLayout_textBox} from "../layout/TextBoxLayout";
import {Button} from "./Button";
import {StoryCaption} from "./StoryCaption";

export class TextBox extends Button {

    get text(): string {
        return this.textArea.htmlText;
    }

    set text(value: string) {
        this.textArea.htmlText = value;
        this.textArea.validateText();
        if (this.backgroundVisible) {
            this.resizeOutline();
            this.resizeMask();
        } else {
            this.resizeHitArea();
        }
    }

    get backgroundVisible() {
        return this.textBoxBg.visible;
    }

    set backgroundVisible(value: boolean) {
        if (this.textBoxBg.visible !== value) {
            this.textBoxBg.visible = value;
            this.textBoxOutline.visible = value;
            this.speakerIcon.visible = value;
            if (value) {
                this.resizeOutline();
                this.resizeMask();
            } else {
                this.resizeHitArea();
            }
        }
    }

    public readonly container = this.visuals.addChild(new Container());
    public readonly textBoxBg = this.container.addChild(new Sprite("text-box-bg"));
    public readonly textBoxOutline = this.container.addChild(new NineSlicePlane("text-box-outline", {
        left: 0.04,
        right: 0.04,
    }));
    public readonly speakerIcon = this.container.addChild(new Sprite());
    public readonly bgMask: number[] = [];
    public readonly bgMaskPoints: IPoint[] = [];
    public readonly textArea = this.container.addChild(new StoryCaption("",
        {
            font: {name: Assets.fontGrandstander.assetName, size: 30},
            tint: 0x000000,
            htmlFormat: {em: {color: 0x005e8f}},
            align: "center",
            lineHeight: 1.2,
        },
        Anchor.middleCenter));
    public readonly bgHitArea = this.container.addChild(new Sprite());

    public readonly hoverScale = 1.03;

    private readonly textAreaWidth: number;
    private readonly textOutlineLeft: number;
    private readonly textOutlineRight: number;
    private readonly speakerIconOffset: number;

    constructor() {
        super();

        this.anchor.x = 0.5;
        this.anchor.y = 1;

        TextBoxLayout_textBox.call(this);
        this.container.scale.set(0.5);
        this.textBoxOutline.setScaleByHeight();
        for (let i = 0; i < this.bgMask.length; i += 2) {
            this.bgMaskPoints.push({
                x: this.bgMask[i],
                y: this.bgMask[i + 1],
            });
        }
        this.textBoxBg.mask = this.container.addChild(new Poly(this.bgMaskPoints));

        const anchorX = (this.textArea.anchor as IPoint).x;
        this.textOutlineLeft = this.textArea.x - this.textArea.textAreaWidth * anchorX - this.textBoxOutline.x;
        this.textOutlineRight = this.textBoxOutline.x + this.textBoxOutline.width * this.textBoxOutline.scale.x - this.textArea.x - this.textArea.textAreaWidth * (1 - anchorX);
        this.speakerIconOffset = this.speakerIcon.x - this.textBoxOutline.x;

        // Reduce the text box to make room for the character reaction
        this.textAreaWidth = this.textArea.textAreaWidth;
        this.textArea.textAreaWidth *= 0.9;

        // https://benfrain.com/how-to-get-the-value-of-phone-notches-environment-variables-env-in-javascript-from-css/
        const bottom = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--sab"), 10);
        if (isNaN(bottom) || bottom === 0) {
            this.container.y = 30;
        }
    }

    public getCaptionData() {
        const s = this.container.scale.x;
        return {
            x: this.textArea.x * s + this.container.x,
            y: this.textArea.y * s + this.container.y,
            width: this.textArea.textAreaWidth * s,
            height: this.textArea.textAreaHeight * s,
            anchor: this.textArea.anchor as IPoint,
            lineHeight: this.textArea.lineHeight,
            fontSize: this.textArea.fontSize * s,
        };
    }

    public initVO(vo: string | Array<string | number>, timeout: number = 8, firstCallback?: Callback) {
        timeout = 0; // never replay VO
        this.textArea.initVO(vo, timeout, firstCallback);
        this.onClick.add(() => {
            this.textArea.playVO(vo, timeout);
        });
    }

    public show(cb?: Callback) {
        this.moveYFrom(this.y + this.height, 0.3, Ease.sineOut, cb);
    }

    public getFocusRect(): IRectangle {
        return ContainerExtensions.getGlobalBounds.call(this.bgHitArea);
    }

    private resizeOutline() {
        this.textBoxOutline.x = this.textArea.x - this.textArea.width * (this.textArea.anchor as IPoint).x - this.textOutlineLeft;
        this.textBoxOutline.width = (this.textArea.width + this.textOutlineLeft + this.textOutlineRight) / this.textBoxOutline.scale.x;
        this.bgHitArea.x = this.textBoxOutline.x;
        this.bgHitArea.y = this.textBoxOutline.y;
        this.bgHitArea.width = this.textBoxOutline.width * this.textBoxOutline.scale.x;
        this.bgHitArea.height = this.textBoxOutline.height * this.textBoxOutline.scale.y;
        this.speakerIcon.x = this.textBoxOutline.x + this.speakerIconOffset;
    }

    private resizeMask() {
        const padding = (this.textAreaWidth - this.textArea.width) / 2;
        const mask = this.textBoxBg.mask;
        if (mask) {
            mask.destroy();
        }
        this.textBoxBg.mask = this.container.addChild(new Poly(this.bgMaskPoints.map((it) => ({
            x: it.x < 0 ? it.x + padding : it.x - padding,
            y: it.y,
        }))));
    }

    private resizeHitArea() {
        this.bgHitArea.x = this.textArea.x - this.textArea.width / 2;
        this.bgHitArea.y = this.textArea.y - this.textArea.height;
        this.bgHitArea.width = this.textArea.width;
        this.bgHitArea.height = this.textArea.height * 2;
    }
}
